import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, fehlertext, type SessionUser } from "./api";
import { Auswahl, Eingabe, Fehler, Feld, Karte, Knopf, Laedt, Leer, Seitenkopf, Textfeld } from "./ui";
import { sprache } from "./datum";

/**
 * Antraege: Urlaub, Krankmeldung, Zeitkorrektur.
 *
 * Eine Maske fuer beide Seiten. Der Mitarbeiter stellt Antraege und sieht den
 * Stand, die Geschaeftsleitung sieht alle und entscheidet. Zwei getrennte
 * Masken waeren zwei Darstellungen derselben Liste, und die eine wuerde
 * irgendwann vergessen.
 *
 * Der Mitarbeiter kann einen Antrag zurueckziehen, solange niemand entschieden
 * hat. Danach nicht mehr: eine genehmigte Abwesenheit hat das Soll schon
 * gesenkt, und sie im Nachhinein verschwinden zu lassen waere derselbe Fehler
 * wie eine geloeschte Zeitbuchung.
 */

interface Entscheidung {
  am: string;
  von: string | null;
  notiz: string | null;
}

interface Abwesenheit {
  id: string;
  benutzerId: string;
  mitarbeiter: string;
  art: string;
  von: string;
  bis: string;
  halberTagAnfang: boolean;
  halberTagEnde: boolean;
  status: string;
  grund: string | null;
  tage: number;
  entscheidung: Entscheidung | null;
}

interface Korrektur {
  id: string;
  benutzerId: string;
  mitarbeiter: string;
  datum: string;
  von: string;
  bis: string;
  grund: string;
  status: string;
  entscheidung: Entscheidung | null;
}

interface Antwort {
  abwesenheiten: Abwesenheit[];
  korrekturen: Korrektur[];
  offen: number;
}

const ARTEN = ["vacation", "sick", "unpaid", "special", "training"] as const;

export function AntraegeSeite({ user }: { user: SessionUser }) {
  const { t } = useTranslation();
  const istAdmin = user.role === "admin";
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState<"abwesenheit" | "korrektur" | null>(null);

  const laden = useCallback(async () => {
    setDaten(await api.get<Antwort>("/antraege"));
  }, []);

  useEffect(() => {
    void laden().catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [laden, t]);

  async function tun(fn: () => Promise<unknown>) {
    setFehler(null);
    try {
      await fn();
      await laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <>
      <Seitenkopf
        titel={t("antrag.titel")}
        hinweis={istAdmin ? t("antrag.hinweisChef") : t("antrag.hinweis")}
        aktionen={
          <>
            <Knopf onClick={() => setNeu("abwesenheit")}>{t("antrag.neuAbwesenheit")}</Knopf>
            <Knopf art="neben" onClick={() => setNeu("korrektur")}>{t("antrag.neuKorrektur")}</Knopf>
          </>
        }
      />
      <Fehler text={fehler} />

      {/* Die eine Zahl, nach der ein Mitarbeiter fragt, steht ueber der Liste
          und nicht darunter: wie viele Tage habe ich noch. */}
      <Urlaubskonto istAdmin={istAdmin} />

      {!daten ? <Laedt /> : (
        <>
          <h2 className="mt-8 text-lg font-bold">{t("antrag.abwesenheiten")}</h2>
          {daten.abwesenheiten.length === 0 ? (
            <Leer text={t("antrag.leer")} />
          ) : (
            <ul className="mt-3 space-y-2">
              {daten.abwesenheiten.map((a) => (
                <li key={a.id} className="rounded-card bg-white p-4">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-semibold">{t(`antrag.art.${a.art}`)}</span>
                    <span className="tabular-nums">
                      {kurz(a.von)}{a.von !== a.bis ? ` bis ${kurz(a.bis)}` : ""}
                    </span>
                    <span className="text-sm text-leise">
                      {t("antrag.tage", { count: a.tage })}
                    </span>
                    {istAdmin && <span className="text-sm text-leise">{a.mitarbeiter}</span>}
                    <Stand status={a.status} />
                    <div className="ms-auto flex gap-2">
                      {istAdmin && a.status === "requested" && (
                        <>
                          <Knopf onClick={() => void tun(() =>
                            api.patch(`/antraege/abwesenheit/${a.id}`, { entscheidung: "approved" }))}>
                            {t("antrag.genehmigen")}
                          </Knopf>
                          <Knopf art="warnung" onClick={() => void tun(() =>
                            api.patch(`/antraege/abwesenheit/${a.id}`, { entscheidung: "rejected" }))}>
                            {t("antrag.ablehnen")}
                          </Knopf>
                        </>
                      )}
                      {!istAdmin && a.status === "requested" && (
                        <Knopf art="neben" onClick={() => void tun(() =>
                          api.del(`/antraege/abwesenheit/${a.id}`))}>
                          {t("antrag.zurueckziehen")}
                        </Knopf>
                      )}
                    </div>
                  </div>
                  {a.grund && <p className="mt-1 text-sm text-leise">{a.grund}</p>}
                  {a.entscheidung && (
                    <p className="mt-1 text-sm text-leise">
                      {t("antrag.entschieden", {
                        am: kurz(a.entscheidung.am.slice(0, 10)),
                        von: a.entscheidung.von ?? "?"
                      })}
                      {a.entscheidung.notiz ? `: ${a.entscheidung.notiz}` : ""}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          <h2 className="mt-8 text-lg font-bold">{t("antrag.korrekturen")}</h2>
          {daten.korrekturen.length === 0 ? (
            <Leer text={t("antrag.leer")} />
          ) : (
            <ul className="mt-3 space-y-2">
              {daten.korrekturen.map((k) => (
                <li key={k.id} className="rounded-card bg-white p-4">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-semibold tabular-nums">{kurz(k.datum)}</span>
                    <span className="tabular-nums">{k.von} bis {k.bis}</span>
                    {istAdmin && <span className="text-sm text-leise">{k.mitarbeiter}</span>}
                    <Stand status={k.status} />
                    <div className="ms-auto flex gap-2">
                      {istAdmin && k.status === "requested" && (
                        <>
                          <Knopf onClick={() => void tun(() =>
                            api.patch(`/antraege/zeitkorrektur/${k.id}`, { entscheidung: "approved" }))}>
                            {t("antrag.genehmigen")}
                          </Knopf>
                          <Knopf art="warnung" onClick={() => void tun(() =>
                            api.patch(`/antraege/zeitkorrektur/${k.id}`, { entscheidung: "rejected" }))}>
                            {t("antrag.ablehnen")}
                          </Knopf>
                        </>
                      )}
                      {!istAdmin && k.status === "requested" && (
                        <Knopf art="neben" onClick={() => void tun(() =>
                          api.del(`/antraege/zeitkorrektur/${k.id}`))}>
                          {t("antrag.zurueckziehen")}
                        </Knopf>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-leise">{k.grund}</p>
                  {k.entscheidung && (
                    <p className="mt-1 text-sm text-leise">
                      {t("antrag.entschieden", {
                        am: kurz(k.entscheidung.am.slice(0, 10)),
                        von: k.entscheidung.von ?? "?"
                      })}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {neu === "abwesenheit" && (
        <AbwesenheitDialog onClose={() => setNeu(null)} onFertig={() => { setNeu(null); void laden(); }} />
      )}
      {neu === "korrektur" && (
        <KorrekturDialog onClose={() => setNeu(null)} onFertig={() => { setNeu(null); void laden(); }} />
      )}
    </>
  );
}

function Stand({ status }: { status: string }) {
  const { t } = useTranslation();
  const stil = status === "approved" ? "bg-green-100 text-green-900"
    : status === "rejected" ? "bg-red-100 text-red-800"
      : status === "cancelled" ? "bg-grund text-leise"
        : "bg-marke/30";
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${stil}`}>
      {t(`antrag.status.${status}`)}
    </span>
  );
}

function AbwesenheitDialog({ onClose, onFertig }: { onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation();
  const [art, setArt] = useState<string>("vacation");
  const [von, setVon] = useState("");
  const [bis, setBis] = useState("");
  const [halbAnfang, setHalbAnfang] = useState(false);
  const [halbEnde, setHalbEnde] = useState(false);
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.post("/antraege/abwesenheit", {
        art, von, bis: bis || von,
        halberTagAnfang: halbAnfang, halberTagEnde: halbEnde,
        grund: grund || null
      });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={t("antrag.neuAbwesenheit")} onClose={onClose}>
      <Fehler text={fehler} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("antrag.artLabel")}>
          <Auswahl value={art} onChange={(e) => setArt(e.target.value)}>
            {ARTEN.map((a) => <option key={a} value={a}>{t(`antrag.art.${a}`)}</option>)}
          </Auswahl>
        </Feld>
        <div />
        <Feld label={t("antrag.von")}>
          <Eingabe type="date" value={von} onChange={(e) => setVon(e.target.value)} />
        </Feld>
        <Feld label={t("antrag.bis")} hinweis={t("antrag.bisHinweis")}>
          <Eingabe type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
        </Feld>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={halbAnfang} onChange={(e) => setHalbAnfang(e.target.checked)} />
          {t("antrag.halberErster")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={halbEnde} onChange={(e) => setHalbEnde(e.target.checked)} />
          {t("antrag.halberLetzter")}
        </label>
        <Feld label={t("antrag.grund")} breit>
          <Textfeld rows={2} value={grund} onChange={(e) => setGrund(e.target.value)} />
        </Feld>
      </div>
      <div className="mt-5 flex gap-2">
        <Knopf disabled={laeuft || !von} onClick={() => void speichern()}>{t("antrag.einreichen")}</Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

/** Ein Arbeitstag, so weit die Korrektur ihn braucht. */
interface Tagesbuchung {
  id: string;
  art: string;
  beginn: string;
  ende: string | null;
  storniert: { am: string } | null;
}

function KorrekturDialog({ onClose, onFertig }: { onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation();
  const [datum, setDatum] = useState("");
  const [von, setVon] = useState("08:00");
  const [bis, setBis] = useState("17:00");
  const [grund, setGrund] = useState("");
  const [buchungen, setBuchungen] = useState<Tagesbuchung[]>([]);
  const [buchungId, setBuchungId] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  /*
   * Der Antrag muss sagen, welche Buchung er meint.
   *
   * Ohne diesen Bezug entstand bei der Genehmigung ein zweiter Arbeitstag
   * neben dem gestempelten, und die Monatssumme zaehlte beide. Deshalb werden
   * die Buchungen des gewaehlten Tages geladen und die einzige davon gleich
   * vorbelegt, mitsamt ihren Uhrzeiten: korrigiert wird, was dasteht, und
   * nicht ein leeres Formular ausgefuellt.
   */
  useEffect(() => {
    if (!datum) { setBuchungen([]); setBuchungId(""); return; }
    /*
     * Das Ende des Bereichs ist ausschliesslich, deshalb der Folgetag. Mit
     * demselben Datum auf beiden Seiten waere die Antwort immer leer, und der
     * Antrag hinge wieder an keiner Buchung.
     */
    const naechster = new Date(`${datum}T00:00:00Z`);
    naechster.setUTCDate(naechster.getUTCDate() + 1);
    const bisTag = naechster.toISOString().slice(0, 10);

    void api.get<{ zeiten: Tagesbuchung[] }>(`/zeiten?von=${datum}&bis=${bisTag}&art=day`)
      .then((a) => {
        const offen = a.zeiten.filter((z) => !z.storniert);
        setBuchungen(offen);
        if (offen.length === 1) waehlen(offen[0].id, offen);
        else setBuchungId("");
      })
      .catch(() => setBuchungen([]));
  }, [datum]);

  function waehlen(id: string, liste = buchungen) {
    setBuchungId(id);
    const b = liste.find((z) => z.id === id);
    if (!b) return;
    setVon(uhrzeit(b.beginn));
    if (b.ende) setBis(uhrzeit(b.ende));
  }

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.post("/antraege/zeitkorrektur", {
        datum, von, bis, grund, buchungId: buchungId || undefined
      });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={t("antrag.neuKorrektur")} onClose={onClose}>
      <Fehler text={fehler} />
      <p className="mb-4 text-sm text-leise">{t("antrag.korrekturHinweis")}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Feld label={t("antrag.datum")}>
          <Eingabe type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
        </Feld>
        <Feld label={t("zeit.beginn")}>
          <Eingabe type="time" value={von} onChange={(e) => setVon(e.target.value)} />
        </Feld>
        <Feld label={t("zeit.ende")}>
          <Eingabe type="time" value={bis} onChange={(e) => setBis(e.target.value)} />
        </Feld>
      </div>

      {/*
        Welche Buchung gemeint ist. Bei genau einer steht sie schon da und
        wird nur angezeigt; bei mehreren muss gewaehlt werden, sonst
        korrigiert die Genehmigung die falsche.
      */}
      {datum !== "" && (
        <div className="mt-3">
          {buchungen.length === 0 ? (
            <p className="rounded bg-grund px-3 py-2 text-sm">{t("antrag.keineBuchung")}</p>
          ) : buchungen.length === 1 ? (
            <p className="rounded bg-grund px-3 py-2 text-sm">
              {t("antrag.korrigiertBuchung", {
                von: uhrzeit(buchungen[0].beginn),
                bis: buchungen[0].ende ? uhrzeit(buchungen[0].ende) : "…"
              })}
            </p>
          ) : (
            <Feld label={t("antrag.welcheBuchung")}>
              <Auswahl value={buchungId} onChange={(e) => waehlen(e.target.value)}>
                <option value="">{t("antrag.buchungWaehlen")}</option>
                {buchungen.map((b) => (
                  <option key={b.id} value={b.id}>
                    {uhrzeit(b.beginn)} bis {b.ende ? uhrzeit(b.ende) : "…"}
                  </option>
                ))}
              </Auswahl>
            </Feld>
          )}
        </div>
      )}
      <div className="mt-3">
        <Feld label={t("zeit.grund")} hinweis={t("zeit.grundPflicht")}>
          <Textfeld rows={2} value={grund} onChange={(e) => setGrund(e.target.value)} />
        </Feld>
      </div>
      <div className="mt-5 flex gap-2">
        <Knopf
          disabled={
            laeuft || !datum || grund.trim().length < 3
            || (buchungen.length > 1 && !buchungId)
          }
          onClick={() => void speichern()}
        >
          {t("antrag.einreichen")}
        </Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

function Dialog({
  titel, children, onClose
}: { titel: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-full w-full max-w-2xl overflow-y-auto rounded-card bg-white p-6">
        <h2 className="mb-4 text-lg font-bold">{titel}</h2>
        {children}
        <button className="sr-only" onClick={onClose}>schliessen</button>
      </div>
    </div>
  );
}

function kurz(tag: string): string {
  return new Date(`${tag}T12:00:00Z`).toLocaleDateString(sprache(), {
    day: "2-digit", month: "2-digit", year: "2-digit"
  });
}

interface Konto {
  jahr: number;
  einheit: "natural" | "laboral";
  anspruch: number | null;
  uebertrag: number;
  genommen: number;
  beantragt: number;
  verbleibend: number | null;
}

/**
 * Das Urlaubskonto des angemeldeten Menschen.
 *
 * Fuer die Geschaeftsleitung steht hier ihr eigenes; die Konten der anderen
 * haengen im Team an der Person. Das ist Absicht: diese Seite gehoert dem, der
 * sie oeffnet, und eine Auswahlliste ueber alle Kollegen waere hier eine
 * zweite Teamverwaltung an der falschen Stelle.
 */
function Urlaubskonto({ istAdmin }: { istAdmin: boolean }) {
  const { t } = useTranslation();
  const [konto, setKonto] = useState<Konto | null>(null);

  useEffect(() => {
    void api.get<Konto>("/urlaubskonto").then(setKonto).catch(() => setKonto(null));
  }, []);

  if (!konto) return null;

  const zahl = (n: number) => n.toFixed(1).replace(".0", "").replace(".", ",");

  return (
    <div className="mt-4 rounded-card bg-white p-4">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-bold">{t("urlaub.titel", { jahr: konto.jahr })}</span>
        <span className="text-sm text-leise">{t(`urlaub.einheit.${konto.einheit}`)}</span>
      </div>

      {konto.anspruch === null ? (
        /*
         * Ohne hinterlegten Anspruch keine erfundene Null. Sie saehe aus wie
         * "kein Urlaub uebrig" und hiesse in Wahrheit "nie eingetragen".
         */
        <p className="mt-2 text-sm text-leise">
          {istAdmin ? t("urlaub.ohneAnspruchChef") : t("urlaub.ohneAnspruch")}
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
          <Zahl label={t("urlaub.anspruch")} wert={zahl(konto.anspruch + konto.uebertrag)} />
          <Zahl label={t("urlaub.genommen")} wert={zahl(konto.genommen)} />
          <Zahl
            label={t("urlaub.verbleibend")}
            wert={zahl(konto.verbleibend ?? 0)}
            gross
            warnung={(konto.verbleibend ?? 0) < 0}
          />
          {konto.beantragt > 0 && (
            <Zahl label={t("urlaub.beantragt")} wert={zahl(konto.beantragt)} />
          )}
        </div>
      )}
    </div>
  );
}

function Zahl({
  label, wert, gross = false, warnung = false
}: { label: string; wert: string; gross?: boolean; warnung?: boolean }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-leise">{label}</p>
      <p className={`tabular-nums ${gross ? "text-2xl font-bold" : "text-lg font-semibold"} ${
        warnung ? "text-red-700" : ""
      }`}>
        {wert}
      </p>
    </div>
  );
}

/** Aus einem Zeitpunkt wird die Uhrzeit auf Mallorca, ohne Sekunden. */
function uhrzeit(iso: string): string {
  return new Date(iso).toLocaleTimeString("de-DE", {
    timeZone: "Europe/Madrid", hour: "2-digit", minute: "2-digit"
  });
}
